import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import '@app/style/form.scss';

interface VerifyResponse {
  success: boolean;
  error?: string;
}

const API_URL = 'https://api.bojko.co.uk/api';

export const ContactPage = () => {
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const { executeRecaptcha } = useGoogleReCaptcha();

  const submitRecaptcha = async (): Promise<boolean> => {
    if (!executeRecaptcha) {
      return raiseError('reCAPTCHA not yet available');
    }

    let token: string;
    try {
      token = await executeRecaptcha('contact_form');
      if (!token) {
        return raiseError('Failed to obtain reCAPTCHA token');
      }
    } catch (err) {
      return raiseError('Error executing reCAPTCHA', err);
    }

    let data: VerifyResponse;
    try {
      const response = await fetch(`${API_URL}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      data = await response.json() as VerifyResponse;

      if (!response.ok || !data.success) {
        return raiseError(`reCAPTCHA verification failed: ${data.error ?? 'Unknown error'}`);
      }
    } catch (err) {
      return raiseError('Network error during reCAPTCHA verification', err);
    }

    return true;
  }

  const submitForm = async () => {
    const response = await fetch(`${API_URL}/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formState),
    });

    if (!response.ok) {
      const errorData = await response.json() as { error?: string };
      const errorMessage = `Form submission failed: ${errorData.error ?? 'Unknown error'}`;
      return raiseError(errorMessage);
    }
  }

  const raiseError = (message: string, cause?: unknown) => {
    setSubmissionStatus('error');
    setErrorMessage(message);
    console.error(message, cause);
    throw new Error(message, { cause });
  }

  const handleSubmit: React.SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    setSubmissionStatus('submitting');
    void submitRecaptcha()
      .then(() => {
        void submitForm().then(() => {
          console.log('Form data submitted successfully');
          setSubmissionStatus('success');
        }).catch((err) => {
          raiseError('Error submitting form data', err);
        });
      }).catch((err) => {
        raiseError('Error during reCAPTCHA verification', err);
      });
  };

  const contactFields = submissionStatus !== 'success' && (
    <fieldset disabled={submissionStatus === 'submitting'} className={`contact-fields ${submissionStatus === 'error' ? 'contact-fields--error' : ''}`}>
      <legend>{submissionStatus === 'error' ? 'Error' : 'Contact Form'}</legend>

      <div className={`form-block ${formState.name ? 'form-block--has-value' : ''}`}>
        <input type="text" id='contact-form-name' name="name" placeholder="Name" autoComplete='name' required value={formState.name} onChange={(e) => setFormState({ ...formState, name: e.target.value })} />
        <label htmlFor="contact-form-name">Name</label>
      </div>
      <div className={`form-block ${formState.email ? 'form-block--has-value' : ''}`}>
        <input type="email" id='contact-form-email' name="email" placeholder="Email" autoComplete='email' required value={formState.email} onChange={(e) => setFormState({ ...formState, email: e.target.value })} />
        <label htmlFor="contact-form-email">Email</label>
      </div>
      <div className={`form-block ${formState.message ? 'form-block--has-value' : ''}`}>
        <textarea id='contact-form-message' name="message" placeholder="Message" autoComplete='off' required rows={6} value={formState.message} onChange={(e) => setFormState({ ...formState, message: e.target.value })}></textarea>
        <label htmlFor="contact-form-message">Message</label>
      </div>
      <div className='form-block'>
        <button type="submit" className='form-submit-btn' disabled={submissionStatus === 'submitting'}>
          Send
        </button>
      </div>
    </fieldset>
  )

  return (
    <section className="section section--contact">
      <div className="panel">
        <h1>Contact</h1>
        <form className="contact-form" onSubmit={handleSubmit}>
          <fieldset disabled={submissionStatus === 'submitting'}>
            {submissionStatus === 'error' && <p className="error-message">{errorMessage ?? 'There was an error submitting the form. Please try again.'}</p>}
            {submissionStatus === 'success' && <p className="success-message">Form submitted successfully! Thank you for reaching out.</p>}
          </fieldset>

          {contactFields}
        </form>
      </div>
    </section>
  )
}
